import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import enAuth from "./locales/en/auth.json";
import enBilling from "./locales/en/billing.json";
import enChannels from "./locales/en/channels.json";
import enChat from "./locales/en/chat.json";
import enCommon from "./locales/en/common.json";
import enCrm from "./locales/en/crm.json";
import enErrors from "./locales/en/errors.json";
import enHome from "./locales/en/home.json";
import enLanding from "./locales/en/landing.json";
import enOrders from "./locales/en/orders.json";
import enProducts from "./locales/en/products.json";
import enSettings from "./locales/en/settings.json";
import enTeam from "./locales/en/team.json";
import thAuth from "./locales/th/auth.json";
import thBilling from "./locales/th/billing.json";
import thChannels from "./locales/th/channels.json";
import thChat from "./locales/th/chat.json";
import thCommon from "./locales/th/common.json";
import thCrm from "./locales/th/crm.json";
import thErrors from "./locales/th/errors.json";
import thHome from "./locales/th/home.json";
import thLanding from "./locales/th/landing.json";
import thOrders from "./locales/th/orders.json";
import thProducts from "./locales/th/products.json";
import thSettings from "./locales/th/settings.json";
import thTeam from "./locales/th/team.json";
import {
  DEFAULT_LANGUAGE,
  LANGUAGE_STORAGE_KEY,
  type Language,
  resolveLanguage,
  SUPPORTED_LANGUAGES,
} from "./resolve-language";

// Namespaces are registered eagerly. The whole catalogue is a few tens of KB;
// lazy-loading it would buy little and cost a loading state on every screen.
export const resources = {
  th: {
    common: thCommon,
    auth: thAuth,
    landing: thLanding,
    home: thHome,
    channels: thChannels,
    chat: thChat,
    crm: thCrm,
    billing: thBilling,
    orders: thOrders,
    products: thProducts,
    settings: thSettings,
    team: thTeam,
    errors: thErrors,
  },
  en: {
    common: enCommon,
    auth: enAuth,
    landing: enLanding,
    home: enHome,
    channels: enChannels,
    chat: enChat,
    crm: enCrm,
    billing: enBilling,
    orders: enOrders,
    products: enProducts,
    settings: enSettings,
    team: enTeam,
    errors: enErrors,
  },
} as const;

function readStoredLanguage(): string | null {
  try {
    return window.localStorage.getItem(LANGUAGE_STORAGE_KEY);
  } catch {
    // silent: Safari private mode throws on localStorage access; detection
    // simply falls through to the browser preference.
    return null;
  }
}

const initialLanguage = resolveLanguage({
  search: window.location.search,
  stored: readStoredLanguage(),
  navigatorLanguages: window.navigator.languages,
});

void i18n.use(initReactI18next).init({
  resources,
  lng: initialLanguage,
  fallbackLng: DEFAULT_LANGUAGE,
  supportedLngs: [...SUPPORTED_LANGUAGES],
  defaultNS: "common",
  // React escapes for us; letting i18next escape too double-encodes entities.
  interpolation: { escapeValue: false },
});

/**
 * The active language, narrowed to one we support.
 *
 * Safe to call outside React — non-component helpers (formatters) use it so
 * they don't need the language threaded through every call site.
 */
export function getLanguage(): Language {
  return SUPPORTED_LANGUAGES.find((lng) => lng === i18n.resolvedLanguage) ?? DEFAULT_LANGUAGE;
}

/** Switch language and remember the choice. Components re-render via react-i18next. */
export function setLanguage(lng: Language): void {
  void i18n.changeLanguage(lng);
  try {
    window.localStorage.setItem(LANGUAGE_STORAGE_KEY, lng);
  } catch {
    // silent: private mode — the choice applies for this session only.
  }
}

export { i18n };
